<template>
  <article class="news-wrapper" :aria-labelledby="news.id">
    <template v-if="news">
      <header class="sr-only w-100 h-100">
        <h2 :id="news.id"
            class="sr-title"
            v-html="$sanitize(newsScreenReaderOutput)"
        ></h2>
      </header>

      <b-card :id="'wrapper-' + news.id" class="custom-padding mb-0">
        <!-- additional info on the news, that is necessary on specific pages -->
        <slot name="header-info" />

        <!-- Pinned info or additional information -->
        <div v-if="news.is_pinned && !isPreview"
             class="d-flex align-items-center mb-3 no-selection"
             data-cy="pinned-news"
        >
          <sdui-icon class="sdui-icon icon-pin-16" />
          <span class="small gray-300 ml-1">{{ $t('news.pin.title') }}</span>
          <STooltip :title="$t('news.pin.instructions')">
            <template #tooltip-trigger="{ triggerAttrs }">
              <b-button v-bind="triggerAttrs"
                        variant="link"
                        class="text-primary ml-2 mb-0 p-0 mh-0"
              >
                <span class="small">
                  {{ $t('news.pin.button') }}
                </span>
              </b-button>
            </template>
          </STooltip>
        </div>

        <!-- Preview for chat -->
        <b-row v-if="isPreview" no-gutters>
          <b-col>
            <div class="info-wrapper d-flex align-items-center mb-1">
              <sdui-icon class="icon-bell circle text-primary bg-primary-light icon-24" />
              <header class="ml-2">
                <h3 class="h2 fz-base mb-0 font-weight-bold">{{ title || $t('news.preview.alternative_title') }}</h3>
                <p aria-hidden="true" class="gray-300 nano d-block m-0">{{ date }}</p>
              </header>
            </div>
          </b-col>
        </b-row>

        <b-row v-else no-gutters>
          <!-- Display author and target channels -->
          <b-col cols="10" class="author-wrapper">
            <b-badge v-if="news.user"
                     size="lg"
                     :class="author_colors"
                     class="custom-author-badge mb-1"
                     aria-hidden="true"
            >
              <span class="sr-only">{{ $t('news.card.from') }}</span>
              {{ news.user.meta.displayname }}
            </b-badge>

            <b-badge v-else-if="news.is_official"
                     size="lg"
                     class="custom-badge mb-1"
                     variant="primary"
                     aria-hidden="true"
            >
              <span class="sr-only">{{ $t('news.card.from') }}</span>
              {{ $t('news.card.badge.author.sdui') }}
            </b-badge>

            <b-badge v-else
                     size="lg"
                     class="custom-badge mb-1"
                     variant="primary"
                     aria-hidden="true"
            >
              <span class="sr-only">{{ $t('news.card.from') }}</span>
              {{ $t('news.card.badge.author.unknown') }}
            </b-badge>

            <span v-if="news.channels && news.channels.length > 0">
              <!-- need to add +1bottom px because chevron fontsize !=  badge's fontsize-->
              <sdui-icon class="text-muted small icon-chevron-right d-inline-block align-middle position-relative aux-top" />
              <b-badge v-if="news.channels.length === 1"
                       size="lg"
                       class="custom-badge mb-1"
                       variant="secondary"
                       aria-hidden="true"
              >
                <span class="sr-only">{{ $t('news.card.to') }}</span>
                {{ newsReceiver }}
              </b-badge>
              <STooltip v-else :title="news.channels.map((el) => el.meta.displayname).join(', ')">
                <template #tooltip-trigger="{ triggerAttrs }">
                  <b-badge v-bind="triggerAttrs"
                           size="lg"
                           class="custom-badge mb-1"
                           variant="secondary"
                  >
                    <span class="sr-only">{{ $t('news.card.to') }}</span>
                    {{ news.channels[0].meta.displayname }} + {{ additionalChannelsCount }}
                  </b-badge>
                </template>
              </STooltip>
            </span>

            <p aria-hidden="true" class="news-date mb-0 mt-1 small text-muted">{{ date }}</p>
          </b-col>

          <!--Dropdown-Menu-->
          <b-col col
                 xs="2"
                 class="text-right"
          >
            <b-button
              v-if="news.has_translations"
              variant="transparent"
              class="language-indicator reset-button"
              data-cy="news-translation"
              @click="openTranslationModalAndTrack"
            >
              <span class="sr-only">{{ $t('news.card.news_show_translation') }}</span>
              <img :src="imgUrl"
                   alt=""
                   loading="lazy"
              />
            </b-button>
            <SDropDown
              v-if="showActions"
              :id="`sdui-news-dropdown-${news.id}`"
              ref="editDropdown"
              lazy
              right
              variant="link"
              size=""
              no-caret
              data-cy="news-item-dropdown"
            >
              <template #button-content>
                <span data-cy="news-dropdown">
                  <sdui-icon class="icon-more text-muted options-selector" />
                  <span class="sr-only">{{ $t('news.card.actions') }}</span>
                </span>
              </template>

              <template v-if="$can('pin', news)">
                <b-dropdown-item-button data-cy="news-pin" @click="togglePin()">
                  <sdui-icon class="d-inline-block align-middle text-primary" :class="news.is_pinned ? 'icon-unpin-o' : 'icon-pin-o'" />
                  <span v-if="news.is_pinned" class="ml-2">{{ $t('news.pin.options_unpin') }}</span>
                  <span v-else class="ml-2">{{ $t('news.pin.options_pin') }}</span>
                </b-dropdown-item-button>
              </template>

              <template v-if="$can('update', news)">
                <li class="dropdown-divider" aria-hidden="true" />
                <b-dropdown-item-button data-cy="news-edit" @click="editNews()">
                  <sdui-icon class="icon-edit-o d-inline-block align-middle text-primary" />
                  <span class="ml-2">{{ $t('global.edit') }}</span>
                </b-dropdown-item-button>
              </template>

              <template v-if="$can('delete', news)">
                <li class="dropdown-divider" aria-hidden="true" />
                <b-dropdown-item-button data-cy="news-delete" @click="showDeleteModal = true">
                  <sdui-icon class="icon-delete-o d-inline-block align-middle text-primary" />
                  <span class="ml-2">{{ $t('global.delete_for_all') }}</span>
                </b-dropdown-item-button>
              </template>

              <template v-if="canShowSurveyResults">
                <li class="dropdown-divider" aria-hidden="true" />
                <b-dropdown-item-button data-cy="news-survey-evaluation" @click="openVotersList">
                  <sdui-icon class="icon-info-o d-inline-block align-middle text-primary" />
                  <span class="ml-2">{{ $t('survey.options.view_voters') }}</span>
                </b-dropdown-item-button>
              </template>

              <template v-if="news.survey && $can('end', news.survey)">
                <li class="dropdown-divider" aria-hidden="true" />
                <b-dropdown-item-button @click="showEndSurveyConfirmationModal = true">
                  <sdui-icon class="icon-logout d-inline-block align-middle text-primary" />
                  <span class="ml-2">{{ $t('survey.options.end') }}</span>
                </b-dropdown-item-button>
              </template>

              <template v-if="displayContentModerationTools">
                <li class="dropdown-divider" aria-hidden="true" />
                <b-dropdown-item-button data-cy="report-content" @click="$router.push({ name: 'help-send-feedback' })">
                  <sdui-icon class="icon-message-warning-o d-inline-block align-middle text-primary" />
                  <span class="ml-2">{{ $t('content_moderation.report_content') }}</span>
                </b-dropdown-item-button>

                <li class="dropdown-divider" aria-hidden="true" />
                <b-dropdown-item-button
                  data-cy="block-user"
                  :disabled="!news.user?.id"
                  @click="showSoftDeleteUserContentModerationModal(news.user?.id)"
                >
                  <sdui-icon class="icon-user-remove-o d-inline-block align-middle text-primary" />
                  <span class="ml-2">{{ $t('content_moderation.block_user.block_user') }}</span>
                </b-dropdown-item-button>
              </template>

              <li v-if="canShowAnyOtherDropdownItem"
                  class="dropdown-divider"
                  aria-hidden="true"
              />
              <b-dropdown-item-button @click="copyLinkToSingleNews">
                <sdui-icon class="icon-duplicate-o d-inline-block align-middle text-primary" />
                <span class="ml-2"> {{ $t('news.copy_link') }} </span>
              </b-dropdown-item-button>

              <li class="dropdown-divider" aria-hidden="true" />
              <b-dropdown-item-button @click="navigateToSingleNewsAndTrack">
                <sdui-icon class="icon-news-o d-inline-block align-middle text-primary" />
                <span class="ml-2">{{ $t('news.card.go_to_news_detail') }}</span>
              </b-dropdown-item-button>
            </SDropDown>
          </b-col>
        </b-row>

        <!-- News body: -->
        <b-row no-gutters>
          <b-col>
            <!-- TITLE -->
            <h3 v-if="title && !isPreview" class="h2 my-3 text-black font-weight-bolder title-btn f-20">{{ title }}</h3>

            <!-- Preview image with lightbox: -->
            <SLightbox v-if="news.preview"
                       class="my-4"
                       :file-items="galleryItems"
                       :title-img-uuid="news.preview.uuid"
                       :re-fetch-fn="() => getReFetchedItemForLightGallery(news.id)"
                       anchor-container-class="d-block"
                       img-container-class="mb-1 mt-2 preview-img"
                       data-cy="news-feed-title-image-gallery"
            />

            <!-- DESCRIPTION -->
            <div
              :id="'article-' + news.id"
              :class="{ short: shouldExtend && !showMore }"
              class="content"
              :style="{ height: useExtend && contentHeight }"
            >
              <div
                v-line-clamp="isPreview && { maxLines: 3 }"
                class="content inner mb-0"
                :class="{ 'mt-2': !title && !news.preview }"
                v-html="$sanitize(news.content_rendered)"
              />
            </div>

            <b-button
              v-if="shouldExtend"
              class="custom-btn text-decoration-none"
              :class="{ extended: showMore }"
              variant="link"
              block
              @click="toggleShowMore"
            >
              {{ showMore ? $t('global.collapse.less') : $t('global.collapse.more') }}
            </b-button>
          </b-col>
        </b-row>

        <b-col v-if="isPreview" no-gutters>
          <b-row>
            <b-button
              variant="primary-light"
              class="my-3 d-flex align-items-center justify-content-center"
              size="lg"
              block
              @click="$router.push({ name: 'single-news', params: { id: news.id } })"
            >
              {{ $t('news.preview.details_btn') }}
            </b-button>
            <span class="text-center gray-300 nano d-block">
              {{ $t('news.preview.details_hint') }}
            </span>
          </b-row>
        </b-col>

        <!-- Show details only if is not ** Preview ** -->
        <b-row v-else no-gutters>
          <b-col>
            <!-- Gallery: -->
            <div v-if="showThumbnailGallery" class="gallery my-4">
              <SLightbox :file-items="galleryItems"
                         :re-fetch-fn="() => getReFetchedItemForLightGallery(news.id)"
                         anchor-container-class="mr-2"
                         img-container-class="gallery-item"
                         data-cy="news-feed-thumbnail-image-gallery"
              />
            </div>

            <!-- Attachments: -->
            <div v-if="files.length" class="files mb-3 news-attachment">
              <sdui-attachment v-for="(file, i) in files"
                               :key="i"
                               :attachment="file"
                               class="my-2"
              />
            </div>

            <footer>
              <!-- Confirm button: -->
              <div v-if="news.is_confirmable" class="confirm-btn mt-4">
                <span v-if="news.meta.is_confirmed || confirmed" class="text-success-dark d-flex align-items-center">
                  <sdui-icon class="icon-check-o icon-24" />
                  <span class="ml-1">{{ $t('news.card.readers.confirmed') }}</span>
                </span>

                <b-button
                  v-if="!confirmed && $can('confirm', news)"
                  v-btn-focus-restore-after-disable
                  type="submit"
                  :disabled="$wait.waiting(`confirming-${news.id}`)"
                  block
                  variant="primary"
                  @click="confirmItem"
                >
                  {{ $t('news.confirm_btn') }}
                </b-button>
              </div>

              <!-- Statistics data: -->
              <b-row
                v-if="news.is_confirmable && stats && $can('view-statistics', news)"
                no-gutters
                align-v="center"
                class="statistics mt-3 pt-3"
              >
                <b-col cols="6">
                  <div class="chart-outer">
                    <p aria-hidden="true" class="h3 count">{{ chartPercent }}%</p>
                    <p class="d-block w-100 h-100 sr-only text-wrap">
                      {{ $t('news.card.readers.confirmed_by_percentage', { percentage: chartPercent }) }}
                    </p>
                    <div class="count bg" aria-hidden="true" />
                    <div class="chart-wrapper" aria-hidden="true">
                      <sdui-pie-chart :stats="chartData" />
                    </div>
                  </div>
                </b-col>

                <b-col cols="">
                  <p class="small text-muted mb-0">{{ $t('news.card.readers.title') }}</p>
                  <div class="position-relative">
                    <p aria-hidden="true" class="small mb-0">
                      <span class="h2">{{ stats.confirmed || 0 }}</span> {{ $t('news.card.readers.of') }} {{ stats.total }}
                    </p>
                    <p class="w-100 h-100 sr-only top-0">{{ stats.confirmed || 0 }} {{ $t('news.card.readers.of') }} {{ stats.total }}</p>
                  </div>
                  <b-button
                    class="btn news-button mt-2 readers-list"
                    block
                    variant="secondary"
                    name="news-reading-confirmation"
                    @click="isReaderslistShown = true"
                  >
                    {{ $t('news.reading_list_btn') }}
                  </b-button>
                </b-col>
              </b-row>

              <!-- Survey: -->
              <div v-if="news.survey" class="mt-3">
                <sdui-multi-question-survey
                  :survey="news.survey"
                  :input-prefix="inputPrefix"
                  :is-translation-mode="isTranslationMode"
                  @update="updateSurvey"
                />
              </div>
            </footer>

            <!--Readers list with scrollable table:-->
            <SModal v-model="isReaderslistShown" :header-title="$t('news.readers_list.modal.title')">
              <sdui-readers-list
                v-if="isReaderslistShown"
                :meta="news.meta"
                :item-id="news.id"
                :total="stats.total"
                :read-by="stats.confirmed"
                :published-at="news.published_at"
                :attachment-count="news.attachments ? news.attachments.length : 0"
                :has-survey="!!news.survey"
              />
            </SModal>
          </b-col>
        </b-row>
      </b-card>
    </template>

    <SConfirmModal
      v-model="showDeleteModal"
      :title="$t('news.card.delete.modal.title')"
      :ok-title="$t('news.card.delete.modal.submit')"
      :cancel-title="$t('news.card.delete.modal.cancel')"
      :return-focus="`#sdui-news-dropdown-${news.id}__BV_toggle_`"
      @ok="deleteItem"
      @hide="showDeleteModal = false"
    />

    <SModal v-model="showTranslations" :header-title="$t('news.translations.modal.title')">
      <sdui-news-translation v-if="showTranslations"
                             :news="news"
                             @update="updateSurvey"
      />
    </SModal>

    <SConfirmModal
      v-model="showEndSurveyConfirmationModal"
      :title="$t('survey.confirm_delete.title')"
      :ok-title="$t('survey.confirm_delete.submit_btn')"
      :cancel-title="$t('survey.confirm_delete.cancel_btn')"
      :return-focus="`#sdui-news-dropdown-${news.id}__BV_toggle_`"
      @ok="endSurvey"
      @hide="showEndSurveyConfirmationModal = false"
    />

    <SConfirmModal
      v-model="isBlockUserConfirmModalDisplayed"
      :title="$t('content_moderation.block_user.block_user')"
      :description="$t('content_moderation.block_user.modal.description')"
      :ok-title="$t('content_moderation.block_user.modal.submit')"
      :cancel-title="$t('content_moderation.block_user.modal.cancel')"
      :return-focus="`#sdui-news-dropdown-${news.id}__BV_toggle_`"
      @ok="softDeleteUserContentModeration"
      @hide="
        isBlockUserConfirmModalDisplayed = false;
        softDeleteUserId = undefined;
      "
    />
  </article>
</template>

<script>
import { defineAsyncComponent, watch } from 'vue'
import { useRouter } from 'vue-router'
import SduiAttachment from 'src/components/news/SduiAttachment.vue'
import SduiReadersList from 'src/components/news/SduiReadersList.vue'
import EntityMixin from 'src/mixins/miscellaneous/entityMixin.js'
import VueScrollTo from 'vue-scrollto'
import { Survey } from 'src/store/models/channel/index.js'
import { User, News } from 'src/store/models/index.js'
import isMobileDeviceMixin from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import STooltip from 'src/components/base/STooltip.vue'
import Users from 'src/api/users.js'
import SduiMultiQuestionSurvey from 'src/components/miscellaneous/ui/surveys/SduiMultiQuestionSurvey.vue'
import SurveyMixin from 'src/mixins/channels/survey/surveyMixin.js'
import { useSduiI18n } from '@/composables/useSduiI18n.ts'
import { useToastNotify } from '@/composables/utils/useToastNotify.ts'
import { useClipboard } from '@vueuse/core'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'
import Channels from '@/api/channels.js'

const SLightbox = defineAsyncComponent(() => import('@/components/base/SLightbox/SLightbox.vue'))
const SduiNewsTranslation = defineAsyncComponent(() => import('./SduiNewsTranslation.vue'))
const SduiPieChart = defineAsyncComponent(() => import('src/components/miscellaneous/ui/charts/SduiPieChart.vue'))

export default {
  setup(props) {
    const router = useRouter()
    const { toastNotify } = useToastNotify()
    const { t } = useSduiI18n()
    const { copy, copied } = useClipboard()

    const copyLinkToSingleNews = () => {
      const path = router.resolve({ name: 'single-news', params: { id: props.news.id } }).href
      const fullPath =
        import.meta.env.VITE_LIVE_UPDATE_ENABLED === 'true' ? `${import.meta.env.VITE_APP_URL}${path}` : `${location.origin}${path}`
      copy(fullPath)
      clyTracking.trackNews().action.copy()
    }

    watch(copied, (newValue) => {
      if (!newValue) return
      toastNotify({
        type: 'success',
        text: t('global.clipboard'),
      })
    })

    return {
      copyLinkToSingleNews,
    }
  },

  name: 'SduiNews',
  components: {
    SLightbox,
    SduiMultiQuestionSurvey,
    STooltip,
    SduiNewsTranslation,
    SduiReadersList,
    SduiAttachment,
    SduiPieChart,
  },

  mixins: [EntityMixin, isMobileDeviceMixin, SurveyMixin],
  props: {
    news: {
      type: [News, Object],
      default: () => new News(),
    },

    useExtend: {
      type: Boolean,
      default: true,
    },

    showDetails: {
      type: Boolean,
      default: true,
    },

    showActions: {
      type: Boolean,
      default: true,
    },

    isPreview: {
      type: Boolean,
      default: false,
    },

    inputPrefix: {
      type: String,
      default: 'option',
    },

    isTranslationMode: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    query: {
      page: 0,
    },

    isExtended: false,
    showDeleteModal: false,
    showMore: true,
    isReaderslistShown: false,
    contentHeight: 'auto',
    confirmed: false,
    showTranslations: false,
    showEndSurveyConfirmationModal: false,
    showTooltip: true,
    isBlockUserConfirmModalDisplayed: false,
    softDeleteUserId: undefined,
    isIOS: false,
    isScreenReaderActive: false,
    newsModel: News,
  }),

  computed: {
    additionalChannelsCount() {
      const placeholder = this.news.channels.find(c => c.meta?.is_placeholder)
      if (!placeholder) return this.news.channels.length - 1
      // real visible channels (excluding the first shown and the placeholder) + hidden count
      return this.news.channels.length - 2 + (placeholder.meta.hidden_channels_count || 0)
    },

    imgUrl() {
      if (!this.$me) return
      return `${import.meta.env.BASE_URL}img/flags/${this.$me.locale}.svg`
    },

    shouldExtend() {
      return this.isExtended && this.useExtend && this.showDetails
    },

    chartData() {
      // console.log('comes in calc of data - ', this.news.id)
      return [this.stats.confirmed, this.stats.total - this.stats.confirmed]
    },

    chartPercent() {
      if (!this.stats || this.stats.confirmed === 0) return 0
      const readersPercent = (this.stats.confirmed / this.stats.total) * 100
      return Math.round(readersPercent)
    },

    title() {
      return this.news.title || ''
    },

    content() {
      return this.news.content || ''
    },

    showThumbnailGallery() {
      if (this.galleryItems.length === 0) return false
      // We don't show the thumbnail gallery when there is only one image, and it is also shown as a preview image. Otherwise show it.
      return !(this.galleryItems.length === 1 && this.news.preview?.uuid === this.galleryItems[0].uuid)
    },

    files() {
      return this.news.attachments.filter((file) => !file.type.startsWith('image'))
    },

    stats() {
      if (!(this.news.meta.statistics && this.news.is_confirmable)) return null
      return {
        total: this.news.meta.statistics.confirmed.total,
        confirmed: this.news.meta.statistics.confirmed.current,
      }
    },

    author_colors() {
      if (!this.news.user) {
        return 'badge-secondary custom-badge'
      }
      const color = this.author_props.color
      return [color, `${color}-light-bg`]
    },

    author_props() {
      if (!this.news.user) return { colors: '' }
      return this.getEntityData(new User(this.news.user))
    },

    date() {
      return this.$dateFormat.date_time(this.news.updated_at)
    },

    datetime() {
      return this.$dateFormat.timestamp(this.news.updated_at)
    },

    showActionsDropdown() {
      // only show options if detailed view is enabled AND (user has permissions for actions OR news has translations)
      return (
        this.showDetails &&
        this.news.can &&
        (this.$can('update', this.news) || this.$can('delete', this.news) || this.$can('pin', this.news) || this.news.has_translations)
      )
    },

    // need to be custom since backend does not return correct target.
    newsReceiver() {
      if (!this.news) return
      const { user, channels } = this.news
      const type = channels[0]?.type
      if (type === 'CHAT' && user?.id !== this.$me?.id) return this.$me.meta.displayname
      return this.news.channels[0].meta.displayname
    },

    displayContentModerationTools() {
      // ATTENTION: This should only be displayed for the schools used in Apple/Google testing
      // It is needed so they don't complain about missing Content Moderation Tools although a normal
      // user will never see this
      if (!this.$me?.school.properties.basic_admin_content_moderation || !this.news || this.news.user?.id === this.$me?.id) return
      return true
    },

    canShowSurveyResults() {
      return this.news.survey?.questions?.length
    },

    canShowAnyOtherDropdownItem() {
      return (
        this.displayContentModerationTools ||
        (this.news.survey && this.$can('end', this.news.survey)) ||
        this.canShowSurveyResults ||
        this.$can('delete', this.news) ||
        this.$can('update', this.news) ||
        this.$can('pin', this.news)
      )
    },

    fromNameValue() {
      if (!this.news?.user) return
      if (this.news.user) return this.news.user.meta.displayname
      if (this.news.is_official) return this.$t('news.card.badge.author.sdui')
      return this.$t('news.card.badge.author.unknown')
    },

    toNameValue() {
      if (!this.news?.channels?.length) return
      if (this.news.channels.length === 1) return this.newsReceiver
      return `${this.news.channels[0].meta.displayname} + ${this.additionalChannelsCount}`
    },

    newsScreenReaderOutput() {
      if (this.isIOS) {
        const translated = this.$t('news.screenreader.ios.message_from', { from: this.fromNameValue, to: this.toNameValue })
        return `<time class="d-flex justify-content-center align-center h-100 w-100" dateTime="${this.$dateFormat.timestamp(this.news.updated_at)}">${translated}</time>`
      }

      return this.$t('news.screenreader.android.message_from', {
        from: this.fromNameValue,
        to: this.toNameValue,
        datetime: this.$dateFormat.timestamp(this.news.updated_at),
        date: this.$dateFormat.date_yearlong(this.news.updated_at),
        time: this.$dateFormat.hour_minute(this.news.updated_at),
      })
    },

    galleryItems () {
      // fix for older news entries that have a preview but no attachments (doesn't happen for newly created items)
      if (this.news.preview && !this.news.attachments?.length) {
        return [this.news.preview]
      }

      return this.news.attachments.filter((file) => file.type.startsWith('image'))
    }
  },

  emits: ['delete', 'update', 'update-survey', 'refetch-news'],

  methods: {
    async deleteItem() {
      this.showDeleteModal = false
      await Channels.deleteChannelNewsById(this.news.id)
      await News.delete(this.news.id)
      await News.clearFetchingCache(this.$me.id)
      clyTracking.trackNews().end.delete()
      this.$emit('delete', this.news.id)
    },

    async editNews() {
      await News.insertOrUpdate({ data: { ...this.news, user: null } })
      clyTracking.trackNews().start.edit()
      this.$router.push({ name: 'news-create-channel-selection', params: { id: this.news.id } })
    },

    async togglePin() {
      const res = await News.togglePin(this.news.id, { is_pinned: !this.news.is_pinned })
      clyTracking.trackNews().action.pin({ isPinned: this.news.is_pinned })
      if (!res.data) return
      this.$emit('update', res.data)
      this.$emit('refetch-news')
    },

    async confirmItem() {
      // waiting param needs to be unique which is why we do it like that. waitFor: confirmItem('confirming-' + news.id, async..) is not working.
      try {
        this.$wait.start(`confirming-${this.news.id}`)
        await News.confirmReadStatus(this.news.id)
        this.confirmed = true
      } finally {
        this.$wait.end(`confirming-${this.news.id}`)
      }
    },

    async setContentHeight() {
      if (!this.showDetails) return
      await this.resetNewsOptions()
      // For screen readers, we always display the full content.
      if (this.isScreenReaderActive) return
      const height = document.getElementById(`article-${this.news.id}`)?.clientHeight
      if (height <= 250) return
      this.isExtended = true
      this.showMore = false
      this.contentHeight = `${height}px`
    },

    toggleShowMore() {
      if (this.showMore) return this.scrollTop()
      this.showMore = !this.showMore
    },

    scrollTop() {
      const self = this
      const options = {
        container: 'body',
        easing: 'ease-in',
        offset: -120,
        force: true,
        cancelable: true,
        onDone: function () {
          self.showMore = !self.showMore
        },
      }

      VueScrollTo.scrollTo(`#wrapper-${this.news.id}`, 300, options)
    },

    updateSurvey(survey) {
      this.$emit('update-survey', survey, this.news.id)
    },

    async endSurvey() {
      const res = await Survey.endVoting(this.news.survey_form_id)
      if (!res || !res.data) return
      clyTracking.trackSurvey().action.submit({ questionCount: this.news.survey.questions.length })
      this.$emit('update-survey', res.data, this.news.id)
    },

    async openVotersList() {
      const surveyId = this.news.survey_form_id || this.news.survey_uuid
      await this.$router.replace({ query: { voters_list: surveyId, reference: this.news.id } })
    },

    async resetNewsOptions() {
      this.contentHeight = 'auto'
      this.isExtended = false
      this.showMore = true
      await this.$nextTick()
    },

    navigateToSingleNewsAndTrack() {
      clyTracking.trackNews().interaction.openDetails()
      this.$router.push({ name: 'single-news', params: { id: this.news.id } })
    },

    openTranslationModalAndTrack() {
      this.showTranslations = true
      clyTracking.trackNews().interaction.translate({ trackGlobal: { feature: 'translation' } })
    },

    showSoftDeleteUserContentModerationModal(userIdToDelete) {
      this.isBlockUserConfirmModalDisplayed = true
      this.softDeleteUserId = userIdToDelete
    },

    async softDeleteUserContentModeration() {
      // ATTENTION: This method is being used to fake a "block user" functionality.
      // This is needed for Apple/Google testing.
      if (!this.softDeleteUserId) return
      await Users.deleteUserSoft(this.softDeleteUserId)
      this.$emit('refetch-news')
    },

    async getReFetchedItemForLightGallery(newsId) {
      if (!newsId) return
      const res = await Channels.getChannelNewsById(newsId)
      return res.data?.attachments
    }
  },

  watch: {
    news: {
      handler: async function () {
        await this.setContentHeight()
      },

      immediate: true,
    },

    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
      this.setContentHeight()
    },

    isMobileLandscape(newVal, oldVal) {
      if (oldVal === newVal) return
      this.setContentHeight()
    },

    showDeleteModal(newVal) {
      if (!newVal) return
      clyTracking.trackNews().start.delete()
    },

    showEndSurveyConfirmationModal(newVal) {
      if (!newVal) return
      clyTracking.trackSurvey().start.close()
    },
  },

  beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
  },

  mounted() {
    this.isIOS = this.$store.state.isIOS
  },
}
</script>

<style lang="scss">
// TODO: check why this is not scoped

.aux-top {
  top: -2px;
}

.news-wrapper {
  position: relative;
  margin-bottom: 12px;
}

.info-wrapper {
  .icon {
    min-width: 36px;
  }
}

.without-dropdown {
  margin-right: 2px;
}

.preview-lightbox-img:nth-child(n + 2) {
  display: none;
}

.gallery-item {
  display: inline-block;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 8px;
  width: 62px;
  object-fit: cover;
  height: 62px;
  margin-bottom: 8px;
}

.file-type {
  background: $color-gray-300;
  color: $color-black;
  border-radius: 50%;
  font-size: pxtorem(12);
  padding: 5px 2px;
  text-transform: uppercase;
}

.content {
  overflow: hidden;
  transition: 0.5s;
  white-space: pre-wrap;

  &.short {
    height: 150px !important;
  }

  p {
    margin-bottom: 0;
  }
  ul {
    white-space: initial;
  }
}

.custom-btn {
  position: relative !important;
  padding-bottom: 20px !important;
  border-top: none !important;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 0;
    width: 100%;
    height: 65px;
    background: linear-gradient(rgba($color-white-rgb, 0) 0%, rgba($color-white-rgb, 0.9) 30%, $color-white 100%);
    z-index: -1;
  }

  &.extended::before {
    display: none;
  }

  &.hide {
    margin-top: 0 !important;
    background: $color-white !important;
    padding: 0 !important;
    max-height: 0 !important;
  }
}

.chart-outer {
  height: 100px;
  width: 100px;
  position: relative;
  margin: 0 auto;
  overflow: hidden;

  .count {
    position: absolute;
    height: 80px;
    width: 80px;
    top: 10px;
    left: 10px;
    background-color: $color-white;
    border-radius: 50%;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-around;
    font-size: pxtorem(22);
    font-weight: $font-weight-bolder;
    z-index: 5;

    &.bg {
      height: 100px;
      width: 100px;
      top: 0;
      left: 0;
      background: $color-success-light;
      z-index: 4;
    }
  }
}

.chart-wrapper {
  height: 100px;
  width: 100px;
  position: relative;
  z-index: 4;
}

.card-body {
  padding-top: 10px;
}

.news-button {
  color: $color-gray-300;
  border-radius: 13px;
  font-size: pxtorem(16);
  text-align: center;
  padding: 10px;
}

.dropdown-menu {
  padding-top: 0;
  padding-bottom: 0;
}

.preview-img {
  max-width: 100%;
  width: auto;
  max-height: 650px;
  display: block;
  margin: 0 auto;
  border-radius: 7px;
}

.news-col {
  padding-left: 12px;
  padding-right: 12px;
}

.news-wrapper .custom-padding {
  padding: 12px;
}

.news-wrapper .custom-padding .card-body {
  padding: 0 !important;
}

// TODO: fix naming to align with conventions
.isAuthorized {
  cursor: pointer;
}

// TODO: this should not be necessary, use a utility class instead
.grey {
  color: $color-gray-300;
}

.options-selector {
  display: inline-block;
  vertical-align: middle;
  margin-left: 6px;
}

.author-wrapper {
  max-width: calc(100% - 90px) !important;
}

.author-wrapper + div {
  min-width: 90px;
}

.news-preview {
  border: 1px solid $color-gray-100;
  border-radius: 12px;
  box-shadow: 0px 2px 4px rgba($color-black-rgb, 0.08);

  .author-wrapper {
    max-width: 100% !important;
  }
  .content {
    display: -webkit-box;
    -webkit-box-orient: vertical;

    *:nth-child(n + 3) {
      display: none;
    }
  }
}
</style>

<style scoped lang="scss">
.content:not(.short) {
  .inner {
    position: relative;
    z-index: 1;
  }
}

.sr-title {
  position: relative;
  margin: 0;
  width: 100%;
  height: 100%;
  max-height: 90vh; // avoid SR focusing more than the visible screen, causing layout shift/scroll
}

.title-btn {
  line-height: pxtorem(28) !important;
  letter-spacing: -0.48px; // replicating h2 style
}

.news-date {
  font-variant-numeric: normal; // replicating button style
}

.statistics {
  border-top: 1px solid $color-gray-100;
}
</style>
